import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trpc } from "~/api/trpc";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import useGTM from "~/components/gtmProvider/useGTM";
import TopProgressBar from "~/components/new/TopProgressBar";
import { SelectedStore, useBookingFlowStore } from "~/stores/booking-flow-store";
import { Language, useLanguageStore } from "~/stores/language-store";
import { GoCheckCircle } from "react-icons/go";
import { takeDrawerHandover } from "~/lib/embed";


const BookingConfirmation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // After Klarna, the drawer is reopened here by the standalone funnel, which hands over the store instead of router state.
    const [selectedStore] = useState(() =>
        (location.state as { selectedStore?: SelectedStore } | null)?.selectedStore
        ?? takeDrawerHandover<{ selectedStore: SelectedStore | null }>("/confirmation")?.selectedStore
        ?? null
    );
    const resetBookingFlow = useBookingFlowStore((state) => state.resetBookingFlow);

    const { language } = useLanguageStore();
    const { sendEvent } = useGTM();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getConfirmationConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
    });
    const cmsConfig = cmsConfigData?.config;

    // Reset the booking flow store after confirmation page mounts.
    // We do this here (not in BookingReview) to avoid the guard effect in BookingReview
    // from redirecting to "/" when selectedStore becomes null before navigation completes.
    useEffect(() => {
        window.scroll(0, 0);
        if (!selectedStore) {
            navigate("/");
            return;
        }
        sendEvent(GTMEvent.BOOKING_FUNNEL_CONFIRMATION, {});
        resetBookingFlow();
    }, []);

    // Helper for localized text
    const getText = (de: string | undefined, en: string | undefined, defaultDe: string, defaultEn: string) => {
        if (language === Language.GERMAN) {
            return de ?? defaultDe;
        }
        return en ?? defaultEn;
    };

    return (
        <>
            <TopProgressBar percentageFilled={100} displayString={getText(cmsConfig?.progressDisplayDe, cmsConfig?.progressDisplayEn, 'BESTÄTIGT', 'CONFIRMED')} color="green" />
            <div className='flex flex-col gap-6 min-h-screen px-4 pt-8 pb-16'>
                <div className="relative w-full p-4 rounded-[5px] flex flex-col gap-8 drop-shadow-md">
                    {/* Success Icon and Message */}
                    <div className='flex flex-col items-center gap-4 text-center'>
                        <GoCheckCircle className='w-10 h-10 text-green-600' />
                        <div className='flex flex-col gap-3'>
                            <span className='text-xl text-burgundy font-normal'>
                                {getText(
                                    cmsConfig?.successTitleDe,
                                    cmsConfig?.successTitleEn,
                                    'Buchung bestätigt!',
                                    'Booking confirmed!'
                                )}
                            </span>
                            <span className='text-sm text-burgundy font-normal'>
                                {getText(
                                    cmsConfig?.successMessageDe,
                                    cmsConfig?.successMessageEn,
                                    'Vielen Dank für deine Buchung. Wir freuen uns auf deinen Besuch!',
                                    'Thank you for your booking. We look forward to seeing you!'
                                )}
                            </span>
                            <span className='text-sm text-burgundy/70 font-light'>
                                {getText(
                                    cmsConfig?.emailConfirmationTextDe,
                                    cmsConfig?.emailConfirmationTextEn,
                                    'Eine Bestätigung wurde an deine E-Mail-Adresse gesendet.',
                                    'A confirmation has been sent to your email address.'
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Clinic Image */}
                    {(selectedStore?.images?.length ?? 0) > 0 && (
                        <div className='w-full max-w-sm mx-auto'>
                            <img
                                className='w-full h-full max-h-40 object-cover rounded-lg'
                                src={selectedStore!.images![0]}
                                alt={selectedStore?.name}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BookingConfirmation;
